import {
  onSnapshot,
  type DocumentData,
  type DocumentReference,
  type Query,
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";

// Thin realtime wrappers over onSnapshot — the web equivalent of the iOS
// FirestoreGateway listeners. Consumers pass a parse function so all data
// flows through the typed model converters.

export interface SnapshotState<T> {
  data: T;
  loading: boolean;
  error: string | null;
}

export function useDoc<T>(
  ref: DocumentReference | null,
  parse: (id: string, data: DocumentData) => T | null,
): SnapshotState<T | null> {
  const [state, setState] = useState<SnapshotState<T | null>>({ data: null, loading: ref !== null, error: null });
  const parseRef = useRef(parse);
  parseRef.current = parse;

  const path = ref?.path ?? null;

  useEffect(() => {
    if (!ref) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    setState((previous) => ({ ...previous, loading: true }));
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const data = snapshot.exists() ? parseRef.current(snapshot.id, snapshot.data()) : null;
        setState({ data, loading: false, error: null });
      },
      (error) => {
        setState({ data: null, loading: false, error: error.message });
      },
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return state;
}

export function useCollection<T>(
  query: Query | null,
  parse: (id: string, data: DocumentData) => T | null,
  /** Change this key to re-subscribe when the query object identity changes meaningfully. */
  queryKey: string,
): SnapshotState<T[]> {
  const [state, setState] = useState<SnapshotState<T[]>>({ data: [], loading: query !== null, error: null });
  const parseRef = useRef(parse);
  parseRef.current = parse;
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    const activeQuery = queryRef.current;
    if (!activeQuery) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState((previous) => ({ ...previous, loading: true }));
    const unsubscribe = onSnapshot(
      activeQuery,
      (snapshot) => {
        const items = snapshot.docs
          .map((docSnapshot) => parseRef.current(docSnapshot.id, docSnapshot.data()))
          .filter((item): item is T => item !== null);
        setState({ data: items, loading: false, error: null });
      },
      (error) => {
        setState({ data: [], loading: false, error: error.message });
      },
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey]);

  return state;
}
